import type {
  GamePlayerProjectionTrading,
  GameTeamPlayerProjectionObject,
  TeamPlayerProjectionObject,
  TradingData
} from '@library/types/trading';
import type {
  GamePlayerProjectionsRequest,
  Included,
  Projection,
  ProjectionsAndScoresAPIResponse,
  Status
} from '@library/types/projection';
import {
  formatWithTimezoneChange,
  isTimeAfter,
  isTimeWithinInterval
} from '@library/utils/dateTime';
import { getPromotionValues, isStandard } from '@library/utils';
import { groupBy, map, pluck, uniq } from 'ramda';
import { DateTime } from 'luxon';
import type { LeagueGameData } from '@library/types/games';
import type { LeagueProjectionAttributes } from '@library/types/leagues';
import type { PlayerDescriptors } from '@library/types/player';
import { WS_URL } from '@bo-utils/environmentVariables';
import { defaultTradeSettingAttributes } from '@bo-constants/tradeSettings';
import { findProjectionByProjectionId } from '@bo-utils/projection';
import { getBoardStatus } from '@library/utils/status';
import { getPlayerValues } from '@library/utils/players';
import { hasStandAloneOwnAttributes } from '@bo-utils/leagueAttributes';
import playerImagePlaceholder from '@library/assets/images/icons/player-placeholder.svg';
import { sortByStatType } from '@bo-utils/projectionsByStatType';
import { v4 as uuid } from 'uuid';

export const getTradingProjection = ({
  included,
  projection
}: {
  included: Array<Included<string>>;
  projection: Projection<string>;
}): GamePlayerProjectionTrading => {
  const {
    id: projectionId,
    attributes: {
      auto_trade: autoTrade,
      board_time: boardTime,
      data_source: dataSource,
      description,
      game_id: gameId,
      game_mode_ids: gameModeIds,
      has_suggested_lines: hasSuggestedLines,
      in_game: inGame,
      is_community_play: isCommunityPlay,
      line_score: projectionValue,
      max_probability_of_more: maxProbabilityOfMore,
      min_probability_of_more: minProbabilityOfMore,
      odds_type: oddsType,
      probability_of_more: probabilityOfMore,
      rank,
      start_time: startTime,
      display_time: displayTime,
      stat_type: statType,
      status,
      target_probability_of_more: targetProbabilityOfMore
    },
    relationships: {
      duration: { data: duration },
      league: { data: league },
      stat_type: { data: statTypeData }
    }
  } = projection;

  const leagueData = included.find(
    (include) =>
      include.type === 'league' &&
      projection?.relationships?.league?.data?.id === include.id
  );

  const {
    attributes: { name: leagueName }
  } = leagueData ?? { attributes: { name: '' } };

  const {
    playerId,
    playerImage,
    playerName,
    market,
    foreignId,
    position,
    team,
    teamName
  } = getPlayerValues({
    included,
    projection
  });

  const { discountPercentage, flashSaleLineScore, isPromo, optIn } =
    getPromotionValues({ included, projection });

  return {
    autoTrade,
    boardTime,
    dataSource,
    durationId: duration?.id,
    description,
    discountPercentage,
    flashSaleLineScore,
    foreignId,
    gameId: gameId ?? '',
    gameModeIds,
    hasSuggestedLines,
    isCommunityPlay,
    inGame,
    isPromo,
    leagueId: league?.id ?? undefined,
    leagueName,
    market,
    maxProbabilityOfMore,
    minProbabilityOfMore,
    oddsType,
    optIn,
    playerId,
    playerImage: playerImage ?? playerImagePlaceholder,
    playerName,
    position,
    probabilityOfMore,
    projectionId,
    projectionValue: Number(projectionValue),
    rank,
    startTime,
    displayTime,
    statType,
    statTypeId: statTypeData?.id,
    status,
    targetProbabilityOfMore,
    team,
    teamName
  };
};

export const getPlayersProjectionByGame = ({
  projections,
  perPage = 300
}: {
  projections?: ProjectionsAndScoresAPIResponse;
  perPage?: number;
}): TradingData => {
  if (!projections) {
    return {
      count: 0,
      estimatedTotal: 0,
      tradingGames: {}
    };
  }

  const knownNLSGames: Array<string> = [];

  const determineNLSGameId = ({
    description,
    startTime,
    team
  }: {
    description: string;
    startTime: string;
    team: string;
  }): string => {
    const teamThenDescription = `NLS-${team ?? ''}-${description}-${startTime}`;
    const descriptionThenTeam = `NLS-${description}-${team ?? ''}-${startTime}`;

    const descriptionThenTeamKnownGame =
      knownNLSGames.includes(descriptionThenTeam);

    if (descriptionThenTeamKnownGame) {
      return descriptionThenTeam;
    }

    knownNLSGames.push(teamThenDescription);
    return teamThenDescription;
  };

  const gamePlayerProjectionScore: Array<
    Record<string, Record<string, Record<string, GamePlayerProjectionTrading>>>
  > = projections?.data?.flatMap((projection) => {
    const data = getTradingProjection({
      included: projections.included,
      projection
    });

    const {
      teamName,
      team,
      gameId,
      description,
      durationId,
      leagueId,
      leagueName,
      startTime,
      playerId
    } = data;

    const durationOrLeague = hasStandAloneOwnAttributes({ name: leagueName });
    const durationString = durationOrLeague ? durationId : leagueId;
    const gameString = gameId
      ? String(gameId)
      : determineNLSGameId({ description, startTime, team: team ?? '' });

    const gameIdentifier = `${gameString} duration-${durationString ?? ''}`;

    const teamIdOrName = teamName || team || '';

    if (!teamIdOrName || !playerId) {
      return [];
    }

    return [
      {
        [gameIdentifier]: {
          [teamIdOrName]: {
            [playerId]: data
          }
        }
      }
    ];
  });

  const byGame = groupBy<
    Record<string, Record<string, Record<string, GamePlayerProjectionTrading>>>
  >((item) => {
    const key = Object.keys(item)[0];

    return key;
  });

  const reducedGameTeamPlayer: GameTeamPlayerProjectionObject = {};

  Object.entries(byGame(gamePlayerProjectionScore)).forEach(
    ([gameId, value]) => {
      if (!value) {
        return;
      }

      const mappedValues = value.map((item) => item[gameId]);

      const reducedTeamPlayer: TeamPlayerProjectionObject = {};

      mappedValues.forEach(
        (item: Record<string, Record<string, GamePlayerProjectionTrading>>) => {
          const teamIdOrName = Object.keys(item)[0];
          const teamValue = item[teamIdOrName];
          const playerId = Object.keys(teamValue)[0];
          const existingPlayer =
            reducedTeamPlayer[teamIdOrName]?.[playerId] ?? [];

          reducedTeamPlayer[teamIdOrName] = {
            ...reducedTeamPlayer[teamIdOrName],
            [playerId]: [...existingPlayer, teamValue[playerId]]
          };
        }
      );

      const sortedReducedTeamPlayer = map(
        (team) =>
          map((playerProjections) => sortByStatType(playerProjections), team),
        reducedTeamPlayer
      );

      reducedGameTeamPlayer[gameId] = sortedReducedTeamPlayer;
    }
  );

  let estimatedTotalProjections =
    projections.meta.total_pages > 1
      ? projections.meta.total_pages * perPage
      : projections.data.length;

  let count = 0;
  if (projections.data.length < perPage) {
    if (projections.meta.total_pages === 1) {
      count = projections.data.length;
    } else {
      const endCount =
        projections.data.length + (projections.meta.current_page - 1) * perPage;
      count = endCount;
      estimatedTotalProjections = endCount;
    }
  } else {
    count = perPage * projections.meta.current_page;
  }

  return {
    tradingGames: reducedGameTeamPlayer,
    count,
    estimatedTotal: estimatedTotalProjections
  };
};

export const getTradingTeamBoardStatus = (players: {
  [playerId: string]: Array<GamePlayerProjectionTrading>;
}): Status => {
  const projections = Object.values(players).flatMap(
    (gamePlayerProjections) => {
      const nonPromoProjections = gamePlayerProjections.filter(
        (projection) => !projection.isPromo
      );

      return [...nonPromoProjections];
    }
  );

  const teamStatuses = projections
    ? new Set<Status>(pluck('status', projections))
    : new Set<Status>();

  return getBoardStatus(teamStatuses);
};

export const getTradingTeamBoardTime = (players: {
  [playerId: string]: Array<GamePlayerProjectionTrading>;
}): string | undefined => {
  const projections = Object.values(players).flatMap(
    (gamePlayerProjections) => {
      const nonSuspendedProjections = gamePlayerProjections.filter(
        (projection) => projection.status !== 'suspended'
      );

      return [...nonSuspendedProjections];
    }
  );

  const teamBoardTime = projections?.[0]?.boardTime;

  return teamBoardTime;
};

export const getTradingTeamDisplayTime = (players: {
  [playerId: string]: Array<GamePlayerProjectionTrading>;
}): string | undefined => {
  const projections = Object.values(players).flatMap(
    (gamePlayerProjections) => {
      const nonSuspendedProjections = gamePlayerProjections.filter(
        (projection) => projection.status !== 'suspended'
      );

      return [...nonSuspendedProjections];
    }
  );

  const displayTime = projections?.[0]?.displayTime;

  return displayTime;
};

export const getTradingModifiedProjections = ({
  initialProjections,
  updatedProjections
}: {
  initialProjections: Array<GamePlayerProjectionTrading>;
  updatedProjections: Array<GamePlayerProjectionTrading>;
}): Array<GamePlayerProjectionTrading> =>
  updatedProjections.filter((projection) => {
    const initialProjection = findProjectionByProjectionId({
      projectionArray: initialProjections,
      projectionId: projection.projectionId
    });

    const { draft, isCopied, status: projectionStatus } = projection;

    if (draft && isCopied && projectionStatus === 'removed') {
      return false;
    }

    if (!initialProjection) {
      return true;
    }

    const {
      autoTrade: initialAutoTrade,
      projectionValue: initialProjectionValue,
      boardTime: initialBoardTime,
      rank: initialRank,
      maxProbabilityOfMore: initialMaxProbabilityOfMore,
      minProbabilityOfMore: initialMinProbabilityOfMore,
      status: initialStatus,
      targetProbabilityOfMore: initialTargetProbabilityOfMore,
      gameModeIds: initialGameModeIds
    } = initialProjection;

    const {
      autoTrade: newAutoTrade,
      projectionValue: newProjectionValue,
      boardTime: newBoardTime,
      rank: newRank,
      status: newStatus,
      maxProbabilityOfMore: newMaxProbabilityOfMore,
      minProbabilityOfMore: newMinProbabilityOfMore,
      targetProbabilityOfMore: newTargetProbabilityOfMore,
      gameModeIds: newGameModeIds
    } = projection;

    return (
      initialBoardTime !== newBoardTime ||
      initialProjectionValue !== newProjectionValue ||
      initialRank !== newRank ||
      initialStatus !== newStatus ||
      initialAutoTrade !== newAutoTrade ||
      initialMaxProbabilityOfMore !== newMaxProbabilityOfMore ||
      initialMinProbabilityOfMore !== newMinProbabilityOfMore ||
      initialTargetProbabilityOfMore !== newTargetProbabilityOfMore ||
      initialGameModeIds !== newGameModeIds
    );
  });

export const getTradingModifiedProjectionsChangeSet = ({
  initialProjections,
  updatedProjections
}: {
  initialProjections: Array<GamePlayerProjectionTrading>;
  updatedProjections: Array<Partial<GamePlayerProjectionTrading>>;
}): Array<GamePlayerProjectionTrading> =>
  updatedProjections.flatMap((change) => {
    const { draft, isCopied, status: projectionStatus, projectionId } = change;

    const initialProjection = findProjectionByProjectionId({
      projectionArray: initialProjections,
      projectionId
    });

    if (draft && isCopied && projectionStatus === 'removed') {
      return [];
    }

    if (!initialProjection) {
      const newProjection = change as GamePlayerProjectionTrading;

      return [newProjection];
    }

    return [
      {
        ...initialProjection,
        ...change
      } as GamePlayerProjectionTrading
    ];
  });

export const getTeamName = ({
  team,
  teamName,
  teamMarket
}: {
  team: string;
  teamName: string;
  teamMarket: string;
}) => {
  const teamNameExists = teamName.length > 0;

  if (!teamNameExists) {
    return team;
  }

  return teamName.includes(teamMarket) ? teamName : `${teamMarket} ${teamName}`;
};

const getSampleProjectionFromTeam = ({
  gameData,
  teamName
}: {
  gameData: Record<string, Record<string, GamePlayerProjectionTrading[]>>;
  teamName?: string;
}): GamePlayerProjectionTrading | undefined => {
  if (!teamName) {
    return undefined;
  }

  const team = gameData[teamName];
  const [sampleProjection] = Object.values(team)[0];

  return sampleProjection;
};

const getGameInfoFromTradingGameData = ({
  gameData,
  teamName
}: {
  gameData: Record<string, Record<string, GamePlayerProjectionTrading[]>>;
  teamName: string;
}): {
  currentTeamName: string;
  startTime: string;
  vsTeamName: string;
} => {
  const currentTeamSampleProjection = getSampleProjectionFromTeam({
    gameData,
    teamName
  });

  const { description, market, startTime, team } =
    currentTeamSampleProjection ?? {};

  const currentTeamName = getTeamName({
    team: team ?? '',
    teamName,
    teamMarket: market ?? ''
  });

  return {
    currentTeamName,
    startTime: startTime ?? '',
    vsTeamName: description ?? ''
  };
};

const getGameInfoFromLeagueGameData = ({
  gameInfo,
  teamName
}: {
  gameInfo: LeagueGameData;
  teamName: string;
}): {
  currentTeamName: string;
  startTime: string;
  vsTeamName: string;
} => {
  const {
    away_team: awayTeam,
    away_team_market: awayMarket,
    away_team_name: awayName,
    date_time: startTime,
    display,
    home_team: homeTeam,
    home_team_market: homeMarket,
    home_team_name: homeName
  } = gameInfo;

  const homeTeamName = getTeamName({
    team: homeTeam,
    teamName: homeName,
    teamMarket: homeMarket
  });

  const awayTeamName = getTeamName({
    team: awayTeam,
    teamName: awayName,
    teamMarket: awayMarket
  });

  const isTeamGame = homeTeamName.length > 0 || awayTeamName.length > 0;

  const isHomeTeam = homeTeamName.includes(teamName);

  const currentTeamName = isHomeTeam ? homeTeamName : awayTeamName;
  const vsTeamName = isHomeTeam ? awayTeamName : homeTeamName;

  return {
    currentTeamName: isTeamGame ? currentTeamName : display,
    startTime,
    vsTeamName: isTeamGame ? vsTeamName : startTime
  };
};

const getGameInfo = ({
  gameData,
  gameInfo,
  teamName
}: {
  gameData: Record<string, Record<string, GamePlayerProjectionTrading[]>>;
  gameInfo?: LeagueGameData;
  teamName: string;
}): {
  currentTeamName: string;
  startTime: string;
  vsTeamName: string;
} => {
  return gameInfo
    ? getGameInfoFromLeagueGameData({ gameInfo, teamName })
    : getGameInfoFromTradingGameData({ gameData, teamName });
};

export const determineTeamHeaders = ({
  gameData,
  gameInfo,
  teamName
}: {
  gameData: Record<string, Record<string, GamePlayerProjectionTrading[]>>;
  gameInfo?: LeagueGameData;
  teamName: string;
}) => {
  const { currentTeamName, startTime, vsTeamName } = getGameInfo({
    gameData,
    gameInfo,
    teamName
  });

  const isMoreThanOneWeekAfter = isTimeAfter({
    dateValue: DateTime.fromISO(startTime),
    dateToCompareAgainst: DateTime.now().plus({ weeks: 1 })
  });

  const pattern = isMoreThanOneWeekAfter ? 'DD - ccc t ZZZZ' : 'ccc t ZZZZ';

  const timeZoneDateTime =
    startTime.length > 0
      ? formatWithTimezoneChange(DateTime.fromISO(startTime), pattern)
      : startTime;

  const secondaryHeader = `@ ${vsTeamName} | ${timeZoneDateTime}`;

  return {
    primaryHeader: currentTeamName,
    secondaryHeader
  };
};

export const getModifiedTradingDashProjections = ({
  initialTradingGames,
  updatedTradingGames
}: {
  initialTradingGames: GameTeamPlayerProjectionObject;
  updatedTradingGames: Array<Partial<GamePlayerProjectionTrading>>;
}): Array<
  GamePlayerProjectionTrading & { gameId: string; playerId: string }
> => {
  const initialProjections = Object.entries(initialTradingGames).flatMap(
    ([gameId, teams]) => {
      return Object.values(teams).flatMap((players) => {
        return Object.entries(players).flatMap(([playerId, projections]) => {
          return projections.map((projection) => {
            return {
              ...projection,
              gameId,
              playerId
            };
          });
        });
      });
    }
  );

  return updatedTradingGames.flatMap((tradingChanges) => {
    const { gameModeIds, projectionId, projectionValue, status } =
      tradingChanges;

    const initialProjection = findProjectionByProjectionId({
      projectionArray: initialProjections,
      projectionId
    });

    const allChangesUndefined = [gameModeIds, projectionValue, status].every(
      (value) => value === undefined
    );

    if (!initialProjection || !projectionId || allChangesUndefined) {
      return [];
    }

    return [
      {
        ...initialProjection,
        ...tradingChanges
      }
    ];
  });
};

export const findProjection = ({
  gameId,
  playerId,
  projectionId,
  teamName,
  tradingGames
}: {
  gameId: string;
  playerId: string;
  projectionId: string;
  teamName: string;
  tradingGames: GameTeamPlayerProjectionObject;
}): GamePlayerProjectionTrading | undefined => {
  const affectedPlayer = tradingGames?.[gameId]?.[teamName]?.[playerId];

  const affectedProjection = findProjectionByProjectionId({
    projectionArray: affectedPlayer,
    projectionId
  });

  return affectedProjection;
};

export const generateBaseProjection = ({
  autoTrade = true,
  boardTime,
  dataSource,
  description,
  draft = true,
  durationId,
  foreignId,
  gameId,
  gameModeIds,
  startTime,
  displayTime,
  inGame = false,
  isCommunityPlay = false,
  isCopied = false,
  playerDescriptors,
  playerId,
  position,
  probabilityOfMore = 0.5,
  projectionValue = '',
  projectionId = '',
  rank = 0,
  simsProbabilityOfMore = 0,
  statType = '',
  statTypeId = undefined,
  statTypes,
  status,
  team,
  teamName
}: {
  autoTrade?: boolean;
  boardTime: string;
  dataSource: 'manual' | 'simulation' | 'auto';
  description: string;
  draft?: boolean;
  durationId: string;
  foreignId?: string;
  gameId: string;
  gameModeIds?: Array<number>;
  startTime: string;
  displayTime: string;
  inGame?: boolean;
  isCommunityPlay?: boolean;
  isCopied?: boolean;
  playerDescriptors: PlayerDescriptors;
  playerId?: string;
  position: string;
  probabilityOfMore?: number;
  projectionId?: string;
  projectionValue?: number | '';
  rank?: number;
  simsProbabilityOfMore?: number;
  statType?: string;
  statTypeId?: string;
  statTypes?: LeagueProjectionAttributes[];
  status?: Status;
  team?: string;
  teamName?: string | null;
}): GamePlayerProjectionTrading => {
  const { playerImage, playerName } = playerDescriptors;
  const [firstStatType] = statTypes ?? [];
  const {
    leagueProjectionFilterId: firstStatTypeId,
    leagueProjectionFilterName: firstStatTypeName
  } = firstStatType ?? {};

  const statusToSet = isTimeAfter({
    dateValue: DateTime.fromISO(startTime)
  })
    ? 'pre_game'
    : 'in_progress';

  const {
    maxProbabilityOfMore,
    minProbabilityOfMore,
    targetProbabilityOfMore
  } = defaultTradeSettingAttributes.standard;

  return {
    autoTrade,
    boardTime,
    dataSource,
    description,
    discountPercentage: '',
    durationId,
    draft,
    foreignId: foreignId ?? '',
    gameId,
    gameModeIds,
    hasSuggestedLines: false,
    inGame,
    isCommunityPlay,
    isCopied,
    isPromo: false,
    maxProbabilityOfMore,
    minProbabilityOfMore,
    oddsType: 'standard',
    playerId,
    playerImage,
    playerName,
    position,
    probabilityOfMore,
    projectionId: projectionId || uuid(),
    projectionValue: projectionValue ?? '',
    rank,
    simsProbabilityOfMore,
    statType: statTypeId ? statType : firstStatTypeName,
    statTypeId: statTypeId ?? firstStatTypeId,
    status: status ?? statusToSet,
    startTime,
    displayTime,
    targetProbabilityOfMore,
    team,
    teamName
  };
};

export const getTradingGameStartTimes = ({
  gameData
}: {
  gameData: Record<string, Record<string, GamePlayerProjectionTrading[]>>;
}): {
  standardStartTimes: Array<string>;
  uniqueStartTimes: Array<string>;
} => {
  const standardStartTimes: Array<string> = [];
  const gameStartTimes: Array<string> = [];

  Object.values(gameData).forEach((teamData) => {
    Object.values(teamData).forEach((playerProjections) => {
      playerProjections.forEach((projection) => {
        const { isPromo, oddsType, startTime } = projection;

        if (isPromo) {
          return;
        }

        if (isStandard(oddsType)) {
          standardStartTimes.push(startTime);
        }

        gameStartTimes.push(startTime);
      });
    });
  });

  return {
    standardStartTimes: uniq(standardStartTimes),
    uniqueStartTimes: uniq(gameStartTimes)
  };
};

export const isRelevantWSMessage = ({
  arg,
  projectionData
}: {
  arg: GamePlayerProjectionsRequest;
  projectionData: { data: Projection<string>; included: Included<string>[] };
}): boolean => {
  const { data, included } = projectionData;

  const {
    attributes: {
      description,
      game_id: gameId = '',
      start_time: startTime,
      stat_type: projectionStatType,
      status
    },
    relationships: {
      duration: { data: duration },
      league: { data: league },
      stat_type: { data: statType }
    }
  } = data;

  const player = included.find(
    (projectionRelation) => projectionRelation.type === 'new_player'
  );

  if (!player) {
    return false;
  }

  const { attributes: playerAttr } = player;
  const { market, name, team = '', team_name: teamName } = playerAttr;

  const fromDate = DateTime.fromISO(arg.fromDate);
  const toDate = arg.toDate && DateTime.fromISO(arg.toDate);
  const messageProjectionDate = DateTime.fromISO(startTime);

  if (
    (toDate
      ? !isTimeWithinInterval({
          start: fromDate,
          end: toDate,
          time: messageProjectionDate
        })
      : isTimeAfter({
          dateValue: fromDate,
          dateToCompareAgainst: messageProjectionDate
        })) ||
    (arg.leagueId &&
      arg.leagueId.length > 0 &&
      !arg.leagueId.includes(league?.id ?? '')) ||
    (arg.durationId &&
      arg.durationId.length > 0 &&
      !arg.durationId.includes(duration?.id ?? '')) ||
    (arg.gameId && !arg.gameId.includes(gameId ?? '')) ||
    (arg.statTypeIds &&
      arg.statTypeIds.length > 0 &&
      !arg.statTypeIds.includes(statType?.id ?? '')) ||
    (arg.status && arg.status.length > 0 && !arg.status.includes(status)) ||
    (arg.team && !arg.team.includes(team)) ||
    (arg.search &&
      !(
        team.toLowerCase().includes(arg.search.toLowerCase()) ||
        name.toLowerCase().includes(arg.search.toLowerCase()) ||
        description.toLowerCase().includes(arg.search.toLowerCase()) ||
        projectionStatType.toLowerCase().includes(arg.search.toLowerCase()) ||
        teamName?.toLowerCase().includes(arg.search.toLowerCase()) ||
        status.toLowerCase().includes(arg.search.toLowerCase()) ||
        market?.toLowerCase().includes(arg.search.toLowerCase())
      ))
  ) {
    return false;
  }

  return true;
};

export const buildWSUrl = () => {
  if (!WS_URL) return '';
  // If the WS_URL is already a valid URL, return it
  if (WS_URL.startsWith('ws://') || WS_URL.startsWith('wss://')) {
    return WS_URL;
  }
  // If the WS_URL is a relative path, build the full URL
  return `${window.location.protocol === 'http:' ? 'ws' : 'wss'}://${
    window.location.host
  }${WS_URL}`;
};
